<template>
  <client-only>
    <component
      :is="dialogComponent"
      v-model="loginDialog"
      :content-class="`auth-bottom-sheet__${sheetPosition} ma-0 !wd-max-h-full <xs:wd-h-full`"
      :max-width="isMobile ? '100%' : 440"
      persistent
      eager
    >
      <v-card class="auth-dialog-container wd-mx-auto wd-w-full sm:(wd-min-w-110)" :class="{ mobile: isMobile }">
        <div class="btn-block text-right mb-2">
          <v-btn v-if="canClose" class="close-btn" :min-width="24" :width="24" :height="24" variant="text" @click="onClose">
            <v-icon :size="20"> mdi-close </v-icon>
          </v-btn>
        </div>

        <div v-show="isTitleBlockShown">
          <div class="login-dalog-title text-center wd-mb-1 wd-pt-10">
            <div class="wd-mb-6 wd-h-11 wd-flex wd-justify-center wd-items-center">
              <v-img v-if="logoImage" class="logo__img mx-auto" :max-height="44" :src="logoImage" />
              <span v-else class="wd-text-olg">{{ branchName }}</span>
            </div>
            <div class="card-header" :class="{ 'caption wd-text-gray80': isIndexStep }">
              <p class="mb-0" :class="{ title: !isIndexStep }">{{ headerTitle }}</p>
              <p v-show="isIndexStep" class="mb-0">{{ $t('use_funnow_member_system') }}</p>
            </div>
          </div>

          <!-- authStep default is 'landing', here is for 3rd part login sdk load -->
          <auth-index
            v-show="authStep === 'index'"
            :ref="stepContentPayload['index'].ref"
            v-bind="stepContentPayload['index'].props"
            v-on="stepContentPayload['index'].events"
          />

          <component
            :is="stepPayload.component"
            v-if="authStep !== 'index'"
            :ref="stepPayload.ref"
            v-bind="stepPayload.props"
            v-on="stepPayload.events"
          />
        </div>

        <consent-step v-if="confirmStep === 'consent'" :loading="isConfirming" @confirm="handleConsentConfirm" />

        <confirm-discard v-else-if="confirmStep === 'confirm-discard'" @confirm="handleDiscardConfirm" @cancel="handleDiscardCancel" />
      </v-card>
    </component>
  </client-only>
</template>

<script>
import { mapGetters } from 'vuex'
import { mapWritableState, mapState } from 'pinia'
import { useEventBus } from '@vueuse/core'
import tools from '@/mixin/tools'

import FunBookV2LoginLanding from '@/components/dialog/FunBookV2LoginLanding.vue'
import AuthIndex from '@/components/dialog/auth/IndexTenants/FunNow/index.vue'
import Register from '@/components/dialog/auth/register.vue'
import Login from '@/components/dialog/auth/login.vue'
import ForgotPassword from '@/components/dialog/auth/forgot-password.vue'
import ConsentStep from '@/components/dialog/auth/consent-step.vue'
import ConfirmDiscard from '@/components/dialog/auth/confirm-discard.vue'
import { LoginEvent } from '~/view-models/event-bus'
import { LoginDialogTrigger, LoginSteps } from '~/view-models/account'
import { usePlatformStore } from '~/store-pinia/platform'
import { useFunBookV2Store } from '~/store-pinia/funbookV2'

export default defineComponent({
  components: {
    FunBookV2LoginLanding,
    AuthIndex,
    Register,
    Login,
    ForgotPassword,
    ConsentStep,
    ConfirmDiscard,
  },
  mixins: [tools],
  setup() {
    const { dialogComponent } = useBottomSheetComponent()
    const { $store: store } = useNuxtApp()
    const authStep = ref(LoginSteps.Landing)
    const loginDialogOptions = computed(() => store.getters['dialog/loginDialogOptions'])
    const isIndexStep = computed(() => authStep.value === LoginSteps.Index)
    const { logoImage, branchName } = storeToRefs(useFunBookV2Store())
    const loginBus = useEventBus(LoginEvent)

    return {
      authStep,
      loginBus,
      loginDialogOptions,
      isIndexStep,
      logoImage,
      branchName,
      dialogComponent,
    }
  },
  data() {
    return {
      confirmStep: '',
      errorMessage: '',
      isMobile: false,
      isConfirming: false,
      pendingConsentCallback: null,
      previousAuthStep: '',
      previousConfirmStep: '',
    }
  },
  computed: {
    isTitleBlockShown() {
      return !this.confirmStep
    },
    stepPayload() {
      return this.stepContentPayload[this.authStep]
    },
    stepContentPayload() {
      return {
        [LoginSteps.Landing]: {
          component: 'FunBookV2LoginLanding',
          ref: null,
          props: {},
          events: {
            success: this.success,
            changeAuthStep: this.changeAuthStep,
          },
        },
        [LoginSteps.Index]: {
          component: 'AuthIndex',
          ref: 'authIndex',
          props: {},
          events: {
            success: this.success,
            changeAuthStep: this.changeAuthStep,
            showConsentStep: this.handleShowConsentStep,
          },
        },
        [LoginSteps.SignUp]: {
          component: 'Register',
          ref: 'registerComponent',
          props: {},
          events: {
            success: this.success,
            changeAuthStep: this.changeAuthStep,
          },
        },
        [LoginSteps.Login]: {
          component: 'Login',
          ref: null,
          props: {},
          events: {
            success: this.success,
            changeAuthStep: this.changeAuthStep,
          },
        },
        [LoginSteps.ForgotPassword]: {
          component: 'ForgotPassword',
          ref: null,
          props: {},
          events: {
            changeAuthStep: this.changeAuthStep,
          },
        },
      }
    },
    sheetPosition() {
      return this.isMobile ? 'default' : 'center'
    },
    canClose() {
      if (this.isConfirming) return false
      return this.loginDialogOptions.canClose ?? true
    },
    headerTitle() {
      switch (this.authStep) {
        case LoginSteps.Landing:
          return this.$t('funbook_login_desc')
        case LoginSteps.SignUp:
          return this.$t('all_register')
        case LoginSteps.Login:
          return this.$t('all_login')
        case LoginSteps.ForgotPassword:
          return this.$t('forgot_pwd_title')
        case LoginSteps.Index:
        default:
          return this.$t('power_by_funnow')
      }
    },
    loginDialog: {
      get() {
        return this.$store.state.dialog.loginDialog
      },
      set(val) {
        this.changeDialogShow(val)
      },
    },
    ...mapGetters({
      user: 'authentication/getUser',
    }),
    ...mapWritableState(usePlatformStore, ['isShowLoginStep']),
    ...mapState(usePlatformStore, ['isTriggerLoginStep']),
  },
  watch: {
    loginDialog: {
      flush: 'post',
      handler(isOpen) {
        if (isOpen) {
          this.addResizeListener()
          this.setDefaultData()
        } else {
          this.isShowLoginStep = false
          this.removeResizeListener()
        }
      },
    },
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      this.removeResizeListener()
    }
  },
  methods: {
    addResizeListener() {
      window.addEventListener('resize', this.onResize, { passive: true })
    },
    removeResizeListener() {
      window.removeEventListener('resize', this.onResize, { passive: true })
    },
    onResize() {
      this.isMobile = window.innerWidth < 600
    },
    setDefaultData() {
      this.authStep =
        this.loginDialogOptions.from === LoginDialogTrigger.OrderDetail || this.isTriggerLoginStep ? LoginSteps.Index : LoginSteps.Landing
    },
    changeAuthStep(step) {
      this.authStep = step
    },
    onClose() {
      // sign-up: 只有在表單 dirty 時才確認是否要放棄註冊
      if (this.authStep === LoginSteps.SignUp) {
        if (this.confirmStep === 'confirm-discard') {
          this.handleDiscardCancel()
          return
        }
        const registerRef = this.stepPayload.ref ? this.$refs[this.stepPayload.ref] : null
        const registerForm = registerRef?.$refs?.registerForm
        if (registerForm?.meta?.dirty) {
          this.previousAuthStep = this.authStep
          this.confirmStep = 'confirm-discard'
          return
        }
      } else if (this.confirmStep === 'consent') {
        this.previousAuthStep = this.authStep
        this.previousConfirmStep = this.confirmStep
        this.confirmStep = 'confirm-discard'
        return
      }
      this.confirmStep = ''
      this.changeDialogShow(false)
    },
    success() {
      if (typeof this.loginDialogOptions.success === 'function') {
        this.loginDialogOptions.success()
      }
      this.loginBus.emit(true, this.loginDialogOptions.from)
      this.$store.dispatch('dialog/setLoginDialog', { state: false })
    },
    changeDialogShow(state) {
      if (!state && !this.user) {
        this.loginBus.emit(false, this.loginDialogOptions.from)
      }
      this.$store.dispatch('dialog/setLoginDialog', { state })
    },
    handleShowConsentStep(callback) {
      this.pendingConsentCallback = callback
      this.confirmStep = 'consent'
      this.loginDialog = true
    },
    async handleConsentConfirm(userContract) {
      if (this.pendingConsentCallback) {
        this.isConfirming = true
        try {
          await this.pendingConsentCallback(userContract)
          this.pendingConsentCallback = null
          this.confirmStep = ''
          this.loginDialog = false
        } finally {
          this.isConfirming = false
        }
      }
    },
    handleDiscardConfirm() {
      this.$refs.authIndex?.resetConsent?.()
      this.pendingConsentCallback = null
      this.confirmStep = ''
      this.previousConfirmStep = ''
      this.loginDialog = false
    },
    handleDiscardCancel() {
      this.authStep = this.previousAuthStep
      this.confirmStep = this.previousConfirmStep
    },
  },
})
</script>
<style scoped lang="scss">
.auth-dialog-container {
  padding: 28px 40px 20px;
  border-radius: 4px;

  .close-btn {
    margin-right: -12px;
  }

  &.mobile {
    border-radius: 0;
  }
}
</style>
<style lang="scss">
.auth-bottom-sheet {
  &__center {
    align-self: center !important;
  }
}

.dna-icon {
  margin-top: 2px;
  cursor: pointer;
}
</style>
